import type { HouseStatus } from "@/types";
import type { LaneId } from "@/types/pipeline";

/**
 * Referenz auf eine andere Komponente aus HOUSE_COMPONENTS.
 *
 * `derived: true` heißt: die Referenz war im Katalog **Freitext** und wurde
 * heuristisch aufgelöst (Namens- statt ID-Treffer, Sammelbegriff, Prosa in der
 * Beschreibung). Die Oberfläche zeichnet solche Kanten gestrichelt und zählt
 * sie sichtbar mit ("n Kanten · m abgeleitet"). Freitext, der sich **nicht**
 * auf genau eine Komponente abbilden lässt ("Alle anderen Repos als Referenz",
 * "Claude Code", "Alle Generator-Tools"), erzeugt bewusst **keine** Referenz —
 * lieber keine Kante als eine geratene.
 */
export type ComponentLink = {
  id: string;
  derived?: boolean;
};

export type HouseComponent = {
  id: string;
  name: string;
  type: string;
  status: HouseStatus;
  path: string;
  description: string;
  keyFiles?: string[];
  technologies?: string[];
  /** Anzeigetext (Prosa, historisch gewachsen) — für Deep-Links siehe dependsOnIds. */
  dependsOn?: string[];
  /** Anzeigetext (Prosa, historisch gewachsen) — für Deep-Links siehe feedsIntoIds. */
  feedsInto?: string[];
  /** Aufgelöste ID-Referenzen "liefert an". Leer = Prosa nicht auflösbar. */
  feedsIntoIds?: ComponentLink[];
  /** Aufgelöste ID-Referenzen "benötigt". Leer = Prosa nicht auflösbar. */
  dependsOnIds?: ComponentLink[];
  skill?: string;
  notes?: string;
  /** Link target inside the webapp (e.g. /artifacts/ebds) */
  drillDown?: string;

  // ── Anbindung an den Datenschritt (scripts/build-data.ts → pipeline.json) ──
  /** Bahn der Prozess-Darstellung. Ohne Wert = spannt mehrere Bahnen bzw. steht daneben. */
  lane?: LaneId;
  /** Kurzname im Bahn-Knoten (der volle `name` ist oft zu lang). */
  laneLabel?: string;
  /** Einzeiler unter dem Knotennamen. */
  laneSub?: string;
  /**
   * Schlüssel, unter dem diese Komponente in INVENTORY.yaml auftaucht
   * (`consumed_by`). Explizit gepflegt, damit der Datenschritt Namen nicht
   * raten muss ("maco-knowledge-to-bpmn" ↔ gen-bpmn).
   */
  inventoryKey?: string;
  /** `publisher`-Werte aus INVENTORY.yaml, die dieser Quell-Komponente gehören. */
  sourcePublishers?: string[];
  /** Run-Stage aus maco-meta/runs/*.json, die diese Komponente belegt. */
  runStage?: string;
};

/**
 * Komponenten-Daten — portiert aus docs/stack.html.
 * Ergänzt um drillDown-Pfade in die Artefakt/Run-Ebenen.
 */
export const HOUSE_COMPONENTS: Record<string, HouseComponent> = {
  "maco-meta": {
    id: "maco-meta",
    name: "maco-meta",
    type: "Governance & Koordination",
    status: "active",
    path: "./maco-meta/",
    description:
      "Single Source of Truth des gesamten MaCo-Ökosystems. Enthält keine produktiven Artefakte – nur Landkarte, Kataster und Link-Index.",
    keyFiles: [
      "docs/REPOS.yaml — Katalog aller Repositories",
      "docs/SKILLS.yaml — Katalog aller AI-Skills",
      "docs/INVENTORY.yaml — Bestandsliste aller regulatorischen Quellen",
      "docs/ARCHITECTURE.md — Architekturprinzipien",
    ],
    technologies: ["YAML", "JSON Schema", "Python (Validierung)", "GitHub Actions"],
    feedsInto: ["Alle anderen Repos als Referenz"],
    // "Alle anderen Repos als Referenz" ist ein Sammelbegriff, keine Komponente:
    // 26 geratene Kanten wären keine Aussage. Governance spannt im Layout alle Bahnen.
    feedsIntoIds: [],
    notes: "CI validiert YAML-Schemas. REPOS.md wird aus REPOS.yaml generiert.",
  },
  "maco-knowledge-mcp": {
    id: "maco-knowledge-mcp",
    name: "maco-knowledge-mcp",
    type: "MCP Server / AI Interface",
    status: "active",
    path: "./maco-knowledge-mcp/",
    description:
      "FastMCP 2.0 Server, der die maco-knowledge-collection als strukturierte Werkzeuge für KI-Agenten bereitstellt.",
    technologies: ["Python 3.11+", "FastMCP 2.0", "SQLite FTS5"],
    dependsOn: ["maco-knowledge-collection"],
    dependsOnIds: [{ id: "maco-knowledge-collection" }],
    feedsInto: ["Claude Code", "Alle Skills"],
    // "Claude Code" und "Alle Skills" sind keine Komponenten des Katasters.
    feedsIntoIds: [],
    notes: "Index ~44 MB. stdio + HTTP Transport. 11 Tools.",
  },
  "maco-runner": {
    id: "maco-runner",
    name: "maco-runner",
    type: "Run-Orchestrator / Job-Queue",
    status: "active",
    path: "./maco-runner/",
    description: "Job-Queue / Autorun / SSE-Backend für die Webapp.",
    technologies: ["Python", "FastAPI", "SQLite", "APScheduler"],
    feedsInto: ["status-webapp"],
    feedsIntoIds: [{ id: "status-webapp" }],
    drillDown: "/activity",
  },
  "camunda": {
    id: "camunda",
    name: "Camunda BPM 7.20",
    type: "Process Engine (extern)",
    status: "external",
    path: "ServiceWorkspace / maco-base",
    description: "Produktive Process Engine, in die alle generierten BPMN/DMN-Artefakte deployed werden.",
    technologies: ["Camunda BPM 7.20", "Docker", "MySQL", "Traefik"],
    lane: "deployment",
    laneLabel: "Camunda BPM 7.20",
    laneSub: "camunda_deploy",
    runStage: "camunda_deploy",
  },
  "ci-cd": {
    id: "ci-cd",
    name: "GitHub Actions CI/CD",
    type: "Continuous Integration / Deployment",
    status: "active",
    path: ".github/workflows/",
    description: "Validierung, Tests, Deployment. Jedes Repo hat eigene Workflows.",
    technologies: ["GitHub Actions", "Docker", "Helm", "bpmnlint", "pytest"],
    lane: "deployment",
    laneLabel: "GitHub Actions",
    laneSub: "je Repo eigene Workflows",
  },
  "helm": {
    id: "helm",
    name: "Helm Chart (maco-deploy-helm)",
    type: "Kubernetes Deployment (extern)",
    status: "external",
    path: "maco-deploy-helm",
    description: "Kapselt die Kubernetes-Deployment-Konfiguration für alle MaCo-Services.",
    technologies: ["Helm", "Kubernetes", "Docker"],
    lane: "deployment",
    laneLabel: "maco-deploy-helm",
    laneSub: "Kubernetes",
  },
  "maco-lf-processes": {
    id: "maco-lf-processes",
    name: "maco-lf-processes",
    type: "Output-Repo — Marktrolle Lieferant",
    status: "active",
    path: "./maco-processes/maco-lf-processes/",
    description: "Deploybare BPMN/DMN-Artefakte für die Marktrolle Lieferant. Organisiert nach YYYYMM-Release.",
    technologies: ["Camunda BPMN XML", "DMN XML", "Docker", "GitHub Actions"],
    feedsInto: ["Camunda BPM"],
    // Namenstreffer statt ID-Treffer ("Camunda BPM" ↔ id "camunda") → abgeleitet.
    feedsIntoIds: [{ id: "camunda", derived: true }],
    lane: "output",
    laneLabel: "maco-lf-processes",
    laneSub: "Lieferant",
    runStage: "process_repo",
  },
  "maco-nb-processes": {
    id: "maco-nb-processes",
    name: "maco-nb-processes",
    type: "Output-Repo — Netzbetreiber",
    status: "partial",
    path: "./maco-processes/maco-nb-processes/",
    description: "BPMN/DMN-Artefakte für Marktrolle Netzbetreiber. Struktur vorhanden.",
    technologies: ["Camunda BPMN XML", "DMN XML"],
    lane: "output",
    laneLabel: "maco-nb-processes",
    laneSub: "Netzbetreiber",
  },
  "maco-msb-processes": {
    id: "maco-msb-processes",
    name: "maco-msb-processes",
    type: "Output-Repo — Messstellenbetreiber",
    status: "partial",
    path: "./maco-processes/maco-msb-processes/",
    description: "BPMN/DMN-Artefakte für Marktrolle Messstellenbetreiber.",
    technologies: ["Camunda BPMN XML", "DMN XML"],
    lane: "output",
    laneLabel: "maco-msb-processes",
    laneSub: "Messstellenbetreiber",
  },
  "gen-bpmn": {
    id: "gen-bpmn",
    name: "knowledge-to-bpmn",
    type: "Generator — BPMN",
    status: "active",
    path: "./maco-knowledge-to-tools/bpmn/",
    description: "Generiert Camunda BPMN aus EBD-JSON. Deterministische 2-Lane-Layout-Engine.",
    skill: "erzeuge-ebd-bpmn",
    drillDown: "/artifacts/ebds",
    feedsInto: ["maco-lf/nb/msb-processes"],
    // Sammelschreibweise "maco-lf/nb/msb-processes" → drei Komponenten, jeweils abgeleitet.
    feedsIntoIds: [
      { id: "maco-lf-processes", derived: true },
      { id: "maco-nb-processes", derived: true },
      { id: "maco-msb-processes", derived: true },
    ],
    lane: "generators",
    laneLabel: "knowledge-to-bpmn",
    laneSub: "EBD → BPMN",
    inventoryKey: "maco-knowledge-to-bpmn",
    runStage: "generate_bpmn",
  },
  "gen-changelog": {
    id: "gen-changelog",
    name: "knowledge-to-changelog",
    type: "Generator — Changelog-Doku",
    status: "active",
    path: "./maco-knowledge-to-tools/changelog/",
    description: "Rendert Changelog-JSON in HTML/Excel/PDF. PDF via Chromium headless.",
    skill: "erzeuge-changelog-doku",
    lane: "generators",
    laneLabel: "knowledge-to-changelog",
    laneSub: "JSON → PDF/Excel",
    inventoryKey: "maco-knowledge-to-changelog",
  },
  "gen-dmn": {
    id: "gen-dmn", name: "knowledge-to-dmn", type: "Generator — DMN (Stub)",
    status: "stub", path: "./maco-knowledge-to-tools/dmn/",
    description: "Soll Camunda DMN-Entscheidungstabellen aus AHB/EBD-Daten generieren. Stub.",
    lane: "generators",
    laneLabel: "knowledge-to-dmn",
    laneSub: "AHB/EBD → DMN (Stub)",
    inventoryKey: "maco-knowledge-to-dmn",
    runStage: "generate_dmn",
  },
  "gen-mapping": {
    id: "gen-mapping", name: "knowledge-to-mapping", type: "Generator — BO4E/EDIFACT (Stub)",
    status: "stub", path: "./maco-knowledge-to-tools/mapping/",
    description: "Soll BO4E ↔ EDIFACT Mapping-Konfigurationen generieren. Stub.",
    lane: "generators",
    laneLabel: "knowledge-to-mapping",
    laneSub: "BO4E ↔ EDIFACT (Stub)",
    inventoryKey: "maco-knowledge-to-mapping",
  },
  "gen-test": {
    id: "gen-test", name: "knowledge-to-test", type: "Generator — Testdaten (Stub)",
    status: "stub", path: "./maco-knowledge-to-tools/test/",
    description: "Soll EDI-Testdaten-Fixtures generieren. Stub.",
    lane: "generators",
    laneLabel: "knowledge-to-test",
    laneSub: "EDI-Testdaten (Stub)",
    inventoryKey: "maco-knowledge-to-test",
  },
  "gen-doc": {
    id: "gen-doc", name: "knowledge-to-doc", type: "Generator — Dokumentation (Stub)",
    status: "stub", path: "./maco-knowledge-to-tools/doc/",
    description: "Soll menschenlesbare Prozessdokumentation generieren. Stub.",
    lane: "generators",
    laneLabel: "knowledge-to-doc",
    laneSub: "Mako-Doku (Stub)",
    inventoryKey: "maco-knowledge-to-doc",
  },
  "gen-adapter": {
    id: "gen-adapter", name: "knowledge-to-adapter", type: "Generator — Camunda Adapter (Stub)",
    status: "stub", path: "./maco-knowledge-to-tools/adapter/",
    description: "Soll Camunda Element-Templates und Adapter-Code generieren. Stub.",
    lane: "generators",
    laneLabel: "knowledge-to-adapter",
    laneSub: "Element-Templates (Stub)",
    inventoryKey: "maco-knowledge-to-adapter",
  },
  "gen-ticket": {
    id: "gen-ticket", name: "knowledge-to-ticket", type: "Generator — Regulatorik-Tickets (Stub)",
    status: "stub", path: "./maco-knowledge-to-tools/ticket/",
    description: "Soll aus Changelog-Daten Ticket-Drafts im User-Story-Format generieren. Stub.",
    // Kein Drill-Down mehr: die Tickets-Seite ist ersatzlos entfallen.
    lane: "generators",
    laneLabel: "knowledge-to-ticket",
    laneSub: "Regulatorik-Tickets",
    inventoryKey: "maco-knowledge-to-ticket",
    runStage: "generate_ticket",
  },
  "gen-validation": {
    id: "gen-validation",
    name: "knowledge-to-validation",
    type: "Generator — Validation-Bundle",
    status: "active",
    path: "./maco-knowledge-to-tools/validation/",
    description:
      "Baut aus maco-knowledge-collection ein Validation-Bundle (MIG + AHB + Conditions/Packages) pro Formatversion. CLI erzeugt JSON+YAML-Artefakt für den Validator-Service.",
    technologies: ["Python 3.11+", "pydantic", "pytest"],
    skill: "erzeuge-validation-bundle",
    dependsOn: ["maco-knowledge-collection"],
    dependsOnIds: [{ id: "maco-knowledge-collection" }],
    feedsInto: ["maco-validation-bundles"],
    feedsIntoIds: [{ id: "maco-validation-bundles" }],
    drillDown: "/validator",
    notes: "12/12 Tests grün, gepusht. CLI: maco-knowledge-to-tools build-validation-bundle <FV>.",
    lane: "generators",
    laneLabel: "knowledge-to-validation",
    laneSub: "MIG + AHB → Bundle",
    inventoryKey: "maco-knowledge-to-validation",
  },
  "maco-validation-bundles": {
    id: "maco-validation-bundles",
    name: "maco-validation-bundles",
    type: "Daten-Repository — Validation-Bundles",
    status: "partial",
    path: "./maco-validation-bundles/",
    description:
      "Versionierte Validation-Bundles je Formatversion (YYYYMM-FVxxxx). Aktuell 1 reales Bundle: FV2610 mit 18 MIG, 275 Prüfidentifikatoren, 767 Conditions, 30 Packages.",
    technologies: ["JSON", "YAML", "Git"],
    dependsOn: ["gen-validation"],
    dependsOnIds: [{ id: "gen-validation" }],
    feedsInto: ["maco-validator"],
    feedsIntoIds: [{ id: "maco-validator" }],
    drillDown: "/validator",
    notes: "Lokal, noch kein Remote — Promote-Pfad via gstack-Skill 'erzeuge-validation-bundle'.",
    lane: "output",
    laneLabel: "maco-validation-bundles",
    laneSub: "FV-Bundles",
  },
  "maco-validator": {
    id: "maco-validator",
    name: "maco-validator",
    type: "Service — EDIFACT-Validierung",
    status: "partial",
    path: "./maco-validator/",
    description:
      "FastAPI-Service. Lädt Validation-Bundles und prüft eingehende EDIFACT-Nachrichten gegen MIG/AHB. Endpoints: GET /bundles, POST /validate.",
    technologies: ["Python 3.11+", "FastAPI", "uvicorn", "pytest"],
    dependsOn: ["maco-validation-bundles"],
    dependsOnIds: [{ id: "maco-validation-bundles" }],
    feedsInto: ["status-webapp /validator"],
    // "status-webapp /validator" nennt Repo + Route in einem String → abgeleitet.
    feedsIntoIds: [{ id: "status-webapp", derived: true }],
    drillDown: "/validator",
    notes: "15/15 Tests grün, lokal — noch kein Docker-Image und kein Remote-Deploy.",
    lane: "deployment",
    laneLabel: "maco-validator",
    laneSub: "lokal · kein Remote-Deploy",
  },
  "maco-regu-parse": {
    id: "maco-regu-parse",
    name: "maco-regu-parse",
    type: "Parser-Monorepo",
    status: "active",
    path: "./maco-regu-parse/",
    description: "Extrahiert Rohdokumente (PDF/DOCX/XLSX) aus maco-energy-mirror in strukturierte JSON. Alle sechs Sub-Parser implementiert (ebd/ahb/mig/codelisten/pruefidentifikatoren/changelog).",
    technologies: ["Python 3.11+", "ebdamame", "kohlrahbi", "migmose", "python-docx", "openpyxl"],
    dependsOn: ["maco-energy-mirror"],
    dependsOnIds: [{ id: "maco-energy-mirror" }],
    feedsInto: ["maco-knowledge-collection"],
    feedsIntoIds: [{ id: "maco-knowledge-collection" }],
    notes: "Vendor-Wrapper bridgen zu Hochfrequenz-Tools via Submodule. codelisten-xlsx liest aktuell DOCX, XLSX-Pfad reserviert (EDI@Energy publiziert keine XLSX-Codelisten).",
    lane: "knowledge",
    laneLabel: "regu-parse",
    laneSub: "Quellen → JSON",
    runStage: "parse",
  },
  "maco-knowledge-collection": {
    id: "maco-knowledge-collection",
    name: "maco-knowledge-collection",
    type: "Daten-Repository — Strukturierte Extrakte",
    status: "partial",
    path: "./maco-knowledge-collection/",
    description: "Zentrales Repository aller strukturierten Extrakte. Organisiert nach Doctype + YYYYMM.",
    drillDown: "/artifacts",
    feedsInto: ["Alle Generator-Tools", "maco-knowledge-mcp"],
    // "Alle Generator-Tools" ist ein Sammelbegriff — die Kanten Collection →
    // Generator kommen aus INVENTORY.yaml (`consumed_by`), nicht aus dieser Prosa.
    feedsIntoIds: [{ id: "maco-knowledge-mcp" }],
    lane: "knowledge",
    laneLabel: "knowledge-collection",
    laneSub: "Doctype × YYYYMM",
  },
  "maco-energy-scraper": {
    id: "maco-energy-scraper",
    name: "maco-energy-scraper",
    type: "Async-Scraper",
    status: "active",
    path: "./maco-energy-scraper/",
    description: "Spiegelt bdew-mako.de (EDI@Energy + Anwendungshilfen) und BNetzA-Festlegungen in den maco-energy-mirror. Async via aiohttp, Router nach file_kind, 50/50 Tests grün.",
    technologies: ["Python 3.11+", "aiohttp", "pydantic"],
    // Aus der Beschreibung abgeleitet (Prosa nennt die drei Herausgeber, aber
    // keine Komponenten-IDs). bdew-bo4e ist bewusst NICHT dabei: die einzige
    // BO4E-Quelle hat in INVENTORY.yaml keinen `scraper` — sie wird nur getrackt.
    dependsOnIds: [
      { id: "src-bdew-mako", derived: true },
      { id: "src-bnetzA", derived: true },
    ],
    feedsInto: ["maco-energy-mirror"],
    feedsIntoIds: [{ id: "maco-energy-mirror" }],
    notes: "Eigener Port des Hochfrequenz edi_energy_scraper, ohne Vendor-Deps. CLI: maco-energy-scraper --mirror <path> --source all (documents+processes+bnetza).",
    lane: "mirror",
    laneLabel: "energy-scraper",
    laneSub: "async · 50/50 Tests",
    runStage: "scrape",
  },
  "maco-energy-mirror": {
    id: "maco-energy-mirror",
    name: "maco-energy-mirror",
    type: "Dokument-Mirror",
    status: "active",
    path: "./maco-energy-mirror/",
    description: "Mirror aller regulatorischen Rohdokumente. ~2500 Dateien aus bdew-mako.de + manuell gepflegte BNetzA-Festlegungen.",
    dependsOn: ["maco-energy-scraper"],
    dependsOnIds: [{ id: "maco-energy-scraper" }],
    feedsInto: ["maco-regu-parse"],
    feedsIntoIds: [{ id: "maco-regu-parse" }],
    lane: "mirror",
    laneLabel: "energy-mirror",
    laneSub: "extracted_to gesetzt",
    runStage: "mirror",
  },
  "status-webapp": {
    id: "status-webapp",
    name: "status-webapp",
    type: "Webapp — Status & Drill-Down",
    status: "active",
    path: "./status-webapp/",
    description:
      "Diese Anwendung. Zeigt Kataster, Artefakt-Inventur, Runs, Queue und Validator; baut ihre Daten zur Build-Zeit aus maco-meta und maco-knowledge-collection (npm run data).",
    keyFiles: [
      "scripts/build-data.ts — Datenschritt (inventory × houseData × runs → pipeline.json)",
      "src/components/house/houseData.ts — Komponenten-Kataster der Architekturseite",
    ],
    technologies: ["React 18", "TypeScript", "Vite", "@conuti-das/prince-ui", "Keycloak"],
    dependsOn: ["maco-meta", "maco-knowledge-collection", "maco-runner", "maco-validator"],
    // maco-meta + maco-knowledge-collection: von scripts/build-data.ts direkt
    // gelesen (belegbar im Code). maco-runner: dessen feedsInto nennt diese
    // Komponente per ID. maco-validator: nennt sie als "status-webapp /validator".
    dependsOnIds: [
      { id: "maco-meta" },
      { id: "maco-knowledge-collection" },
      { id: "maco-runner" },
      { id: "maco-validator", derived: true },
    ],
    feedsIntoIds: [],
    inventoryKey: "status-webapp",
    notes: "Goal CONUT-172. Konsument der Artefakte, kein Generator — taucht in INVENTORY.consumed_by auf, zählt aber nicht in die Generatoren-Bahn.",
  },
  "src-bnetzA": {
    id: "src-bnetzA",
    name: "Bundesnetzagentur (BNetzA)",
    type: "Externe Quelle — Regulierer",
    status: "external",
    path: "https://www.bundesnetzagentur.de/",
    description: "Herausgeberin der Marktregeln GPKE, WiM, MaBiS und BELUGA.",
    lane: "sources",
    laneLabel: "BNetzA",
    laneSub: "GPKE · WiM · MaBiS · BELUGA",
    sourcePublishers: ["Bundesnetzagentur"],
  },
  "src-bdew-mako": {
    id: "src-bdew-mako",
    name: "bdew-mako.de (BDEW)",
    type: "Externe Quelle — BDEW-Portal",
    status: "external",
    path: "https://www.bdew-mako.de/",
    description:
      "Einzige Portalquelle der Marktkommunikation: EBD, AHB, MIG, Codelisten, Prüfidentifikatoren und EDIFACT-Schemas. Hat EDI@Energy als Herausgeber abgelöst — die Dokumentfamilie heißt weiterhin EDI@Energy, veröffentlicht wird sie über bdew-mako.de. Die zuvor unter `edi-energy` geführten zehn Quellen sind 2026-07 hierher umgehängt worden.",
    lane: "sources",
    laneLabel: "bdew-mako.de",
    laneSub: "EBD · AHB · MIG · Codelisten · Prüfids",
    sourcePublishers: ["bdew-mako"],
  },
  "src-bo4e": {
    id: "src-bo4e",
    name: "CONUTI BO4E",
    type: "Externe Quelle — Datenmodell-Standard",
    status: "external",
    path: "https://github.com/conuti-gmbh/bo4e-schema",
    description:
      "Business Objects for Energy (JSON-Schema) — das spartenübergreifende Datenmodell der Branche. Kein BDEW-Erzeugnis, die hier genutzte Fassung ist CONUTIs eigenes Schema-Repository (conuti-gmbh/bo4e-schema), gepflegt im Git statt über ein Portal. Versionierung über eigene Schema-Releases statt Formatwechsel, deshalb `unbefristet` und ohne Scraper (nur getrackt).",
    lane: "sources",
    laneLabel: "CONUTI BO4E",
    laneSub: "CONUTI-Repository · unbefristet",
    sourcePublishers: ["CONUTI"],
  },
};
